import { SET_GLOBAL_FILTER } from '@va/dashboard/actions/ui';
import { getWebsite } from '@va/dashboard/selectors/core';
import { appHistory } from '@va/shared/router';
import { T3_URL_PARAMS } from '@va/standalone/shared/constants';
import { DateRangeKey, TimePeriod } from '@va/types/time';
import {
  getParameterByName,
  getShortcutDateRange,
  isDateRangeKey,
  removeUrlParameter,
  updateUrlParameter,
} from '@va/util/helpers';
import dayjs from 'dayjs';
import { Moment } from 'moment';
import { select } from 'redux-saga/effects';

export function* updateGlobalFilterUrlParam(action: {
  type: typeof SET_GLOBAL_FILTER;
  filter: { from: Moment; until: Moment; unit: TimePeriod; search?: string; dateRangeKey?: DateRangeKey };
}) {
  const { from, until, dateRangeKey } = action.filter;
  const website: { installDate?: number } = yield select(getWebsite);

  // An explicit key is trusted even if the dates were clamped (install date, free plan), since the same clamp is applied again when the URL is opened.
  // Other filter updates (e.g. midnight refresh) carry no key, so the one already in the URL is kept as long as it still describes the same dates.
  const urlPresetKey = getParameterByName(T3_URL_PARAMS.startEndDate);
  const presetKey =
    dateRangeKey ??
    (isDateRangeKey(urlPresetKey) && matchesPreset(urlPresetKey, from, until, website.installDate)
      ? urlPresetKey
      : undefined);

  // Write through the router's history (not raw `window.history.replaceState`) so React Router's internal location stays in sync with the address bar. Prevents out of sync date params (VISA-15097).
  // Params are added before the old ones are removed: the app histories fall back to the current query string when given an empty one, which would bring the removed params back.
  if (presetKey) {
    appHistory.replace(updateUrlParameter(T3_URL_PARAMS.startEndDate, presetKey));
    appHistory.replace(removeUrlParameter([T3_URL_PARAMS.startDate, T3_URL_PARAMS.endDate]));
    return;
  }

  appHistory.replace(updateUrlParameter(T3_URL_PARAMS.startDate, from.format('YYYY-MM-DD')));
  appHistory.replace(updateUrlParameter(T3_URL_PARAMS.endDate, until.format('YYYY-MM-DD')));
  appHistory.replace(removeUrlParameter(T3_URL_PARAMS.startEndDate));
}

function matchesPreset(key: DateRangeKey, from: Moment, until: Moment, installDate: number | undefined) {
  const timezone = from.tz() ?? window.timezone;
  const minDate = installDate ? dayjs(installDate * 1000).tz(timezone) : undefined;
  if (key === 'allTime' && !minDate) return false;

  const { start, end } = getShortcutDateRange(key, minDate, timezone);
  return start.valueOf() === from.valueOf() && end.valueOf() === until.valueOf();
}
